

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  SUCCESS = 2,
  WARN = 3,
  ERROR = 4
}

interface LogStyle {
  badge: string
  badgeBg: string
  badgeColor: string
  textColor: string
}

const LOG_STYLES: Record<LogLevel, LogStyle> = {
  [LogLevel.DEBUG]: {
    badge: 'DEBUG',
    badgeBg: '#4f545c',
    badgeColor: '#ffffff',
    textColor: '#b9bbbe'
  },
  [LogLevel.INFO]: {
    badge: 'INFO',
    badgeBg: '#5865f2',
    badgeColor: '#ffffff',
    textColor: '#dcddde'
  },
  [LogLevel.SUCCESS]: {
    badge: 'SUCCESS',
    badgeBg: '#3ba55c',
    badgeColor: '#ffffff',
    textColor: '#3ba55c'
  },
  [LogLevel.WARN]: {
    badge: 'WARN',
    badgeBg: '#faa61a',
    badgeColor: '#000000',
    textColor: '#faa61a'
  },
  [LogLevel.ERROR]: {
    badge: 'ERROR',
    badgeBg: '#ed4245',
    badgeColor: '#ffffff',
    textColor: '#ed4245'
  }
}

interface LoggerOptions {
  showTimestamp?: boolean
  minLevel?: LogLevel
}

function currentTimestamp(): string {
  const now = new Date()
  const hours = now.getHours().toString().padStart(2, '0')
  const minutes = now.getMinutes().toString().padStart(2, '0')
  const seconds = now.getSeconds().toString().padStart(2, '0')
  const ms = now.getMilliseconds().toString().padStart(3, '0')
  return `${hours}:${minutes}:${seconds}.${ms}`
}

export class Logger {
  private static loggers: Map<string, Logger> = new Map()

  private options: LoggerOptions

  public name: string

  private constructor(name: string, options: LoggerOptions = {}) {
    this.name = name
    this.options = {
      showTimestamp: true,
        minLevel: LogLevel.DEBUG,
      ...options
    }
  }

  public static getInstance(name = 'Borealise'): Logger {
    if (!Logger.loggers.has(name)) {
      Logger.loggers.set(name, new Logger(name))
    }
    return Logger.loggers.get(name)!
  }

  public static create(name: string, options?: LoggerOptions): Logger {
    const logger = new Logger(name, options)
    Logger.loggers.set(name, logger)
    return logger
  }

  public configure(options: Partial<LoggerOptions>): this {
    this.options = { ...this.options, ...options }
    return this
  }

  private buildPrefix(level: LogLevel): unknown[] {
    const style = LOG_STYLES[level]
    const timestamp = this.options.showTimestamp ? currentTimestamp() : null

    const parts: string[] = []
    const styles: string[] = []

    // Timestamp
    if (timestamp) {
      parts.push(`%c${timestamp} `)
      styles.push('color: #72767d; font-size: 10px;')
    }

    // Badge
    parts.push(`%c${style.badge}`)
    styles.push(
      `background: ${style.badgeBg}; color: ${style.badgeColor}; padding: 2px 4px; margin-right: 6px; border-radius: 3px; font-weight: bold; font-size: 10px;`
    )

    // Logger name
    parts.push(`%c[${this.name}]`)
    styles.push('color: #7289da; font-weight: bold;')

    // Message placeholder (will be filled by caller's arguments)
    parts.push('%c')
    styles.push(`color: ${style.textColor};`)

    // Output
    const format = parts.join('')

    return [format, ...styles]
  }

  public get debug(): (message: string, ...args: unknown[]) => void {
    if (LogLevel.DEBUG < (this.options.minLevel ?? LogLevel.DEBUG)) return () => { }
    const prefix = this.buildPrefix(LogLevel.DEBUG)
    return console.log.bind(console, ...prefix)
  }

  public get info(): (message: string, ...args: unknown[]) => void {
    if (LogLevel.INFO < (this.options.minLevel ?? LogLevel.DEBUG)) return () => { }
    const prefix = this.buildPrefix(LogLevel.INFO)
    return console.info.bind(console, ...prefix)
  }

  public get success(): (message: string, ...args: unknown[]) => void {
    if (LogLevel.SUCCESS < (this.options.minLevel ?? LogLevel.DEBUG)) return () => { }
    const prefix = this.buildPrefix(LogLevel.SUCCESS)
    return console.log.bind(console, ...prefix)
  }

  public get warn(): (message: string, ...args: unknown[]) => void {
    if (LogLevel.WARN < (this.options.minLevel ?? LogLevel.DEBUG)) return () => { }
    const prefix = this.buildPrefix(LogLevel.WARN)
    return console.warn.bind(console, ...prefix)
  }

  public get error(): (message: string, ...args: unknown[]) => void {
    if (LogLevel.ERROR < (this.options.minLevel ?? LogLevel.DEBUG)) return () => { }
    const prefix = this.buildPrefix(LogLevel.ERROR)
    return console.error.bind(console, ...prefix)
  }
}